<template>
  <span
    ref="spanRef"
    :class="
      cn(
        'inline-block tabular-nums text-black dark:text-white font-black',
        props.class,
      )
    "
  >
    {{ displayValue }}
  </span>
</template>

<script setup lang="ts">
import { TransitionPresets } from '@vueuse/core'
import { cn } from '~/lib/utils'

type TransitionsPresetsKeys = keyof typeof TransitionPresets

interface NumberTickerProps {
  value: number
  direction?: 'up' | 'down'
  duration?: number
  delay?: number
  decimalPlaces?: number
  class?: string
  transition?: TransitionsPresetsKeys
}

const spanRef = ref<HTMLSpanElement>()
const isHydrated = ref(false)

const props = withDefaults(defineProps<NumberTickerProps>(), {
  value: 0,
  direction: 'up',
  delay: 0,
  duration: 2000,
  decimalPlaces: 0,
  transition: 'easeOutCubic',
})

const transitionValue = ref(props.direction === 'down' ? props.value : 0)

// Format the final value consistently
const formatValue = (val: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: props.decimalPlaces,
    maximumFractionDigits: props.decimalPlaces,
  }).format(Number(val.toFixed(props.decimalPlaces)))
}

// For SSR, show the final value to prevent hydration mismatch
const displayValue = computed(() => {
  if (!isHydrated.value) {
    return formatValue(props.value)
  }
  return formatValue(transitionOutput.value)
})

const transitionOutput = useTransition(transitionValue, {
  delay: props.delay,
  duration: props.duration,
  transition: TransitionPresets[props.transition],
})

const isInView = useElementVisibility(spanRef, {
  threshold: 0,
})

// Only run animation after hydration
onMounted(() => {
  isHydrated.value = true

  // Watch for visibility changes only after hydration
  watch(
    isInView,
    (isVisible) => {
      if (isVisible) {
        transitionValue.value = props.direction === 'down' ? 0 : props.value
      }
    },
    { immediate: true },
  )
})
</script>
